import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAssistantState } from '@assistant-ui/react';

import { AI_CHAT_TOOL_EXPORT_CREATE } from '@atom/graph/ai';
import { Icon, Snackbar } from '@atom/mui';
import {
  AIChatToolExport,
  AIChatToolExportCreateInput,
  AIChatToolIds,
  AIConversationalAnalyticsQueryResult,
} from '@atom/types/ai';
import { downloadFile } from '@atom/utilities/fileUtilities';

import { useChatContext } from '../../ChatContext';
import { useToolCalls } from '../../hooks/useToolCalls';
import { styles } from '../styles';
import { ChatAssistantMessageHookResponse } from '../types';

export const useExportDatasetAction = (): ChatAssistantMessageHookResponse => {
  const {
    threadId,
    ui: { setLoading },
  } = useChatContext();
  const message = useAssistantState(state => state.message);
  const [isExporting, setIsExporting] = useState(false);

  const conversationalAnalyticsQueries =
    useToolCalls<AIConversationalAnalyticsQueryResult>(
      AIChatToolIds.QUERY_CONVERSATIONAL_ANALYTICS,
    ).filter(tool => !tool.isError);

  const query =
    conversationalAnalyticsQueries.length === 1
      ? conversationalAnalyticsQueries[0]
      : null;

  const toolCallId = query?.toolCallId;

  const [createToolExport] = useMutation<
    { aiChatToolExportCreate: AIChatToolExport[] },
    { input: AIChatToolExportCreateInput }
  >(AI_CHAT_TOOL_EXPORT_CREATE);

  const handleExport = useCallback(async () => {
    if (isExporting || !toolCallId) {
      return;
    }

    setIsExporting(true);
    setLoading(true);

    try {
      const response = await createToolExport({
        variables: {
          input: {
            threadId,
            messageId: message.id,
            tool: {
              toolCallId,
              toolName: AIChatToolIds.QUERY_CONVERSATIONAL_ANALYTICS,
            },
          },
        },
      });

      const files = response.data?.aiChatToolExportCreate ?? [];

      if (!files.length) {
        throw new Error(`Failed to get export results`);
      }

      files.forEach(({ fileName, url }) => {
        if (url) {
          downloadFile(url, fileName);
        }
      });
    } catch (error) {
      Snackbar.error({
        message: `Unable to export results. Please try again or contact an administrator`,
      });
    } finally {
      setIsExporting(false);
      setLoading(false);
    }
  }, [
    threadId,
    message.id,
    toolCallId,
    isExporting,
    createToolExport,
    setLoading,
  ]);

  return useMemo<ChatAssistantMessageHookResponse>(
    () => ({
      available: !!toolCallId,
      type: 'menu',
      ui: [
        {
          icon: <Icon style={styles.menuItemIcon}>notes</Icon>,
          copy: 'Export Full Dataset',
          onClick: handleExport,
        },
      ],
    }),
    [toolCallId, isExporting, handleExport],
  );
};
